import { useMutation } from "@tanstack/react-query";

import useNav from "core/hooks/useNav";

import api from "api";

import { useStoreAuth } from "features/auth/store";
import { useStart } from "features/start/fabric";


export const Status = {
	NotDelivered: 0,
	Success: 1,
	Signup: 2
};

const authRequest = async (nav) => {
	const sessionRedirect = sessionStorage.getItem("redirect_uri");
	const redirect = nav.location.search.redirect_uri || sessionRedirect;
	const decoded = decodeURIComponent(redirect);
	const url = new URL(decoded, window.location.origin);
	const params = new URLSearchParams(url.search);
	const ssk = params.get("sub_sk");
	const cid = params.get("sub_cid");
	const ens = await api.profile.ensure({ type: 0, ssk });

	if (sessionRedirect) sessionStorage.removeItem("redirect_uri");
	if (ssk || cid) { /* check if there is a ssk or content id */
		if (ens.balance > 0) nav.open(redirect); /* if profile has tokens, redirect to the redirect uri */
		else {
			const params = ssk ? `sub_sk=${ssk}` : `sub_cid=${cid}`;
			nav.open(`/pay?${params}`); /* otherwise redirect to pay */
		}
	}
	else nav.open(redirect || "/"); /* if no ssk, redirect to redirect or home page */
};

export const useMutateLogin = () => {
	const auth = useStoreAuth();
	const nav = useNav();

	return useMutation({
		mutationKey: ["auth-login"],
		mutationFn: () => {
			const body = { device: auth.device, ...auth.fillAccount() };
			return api.auth.otp(body);
		},
		onSuccess: (data) => {
			auth.setToken(data.token);
			const path = data?.status === Status.Signup ? "signup" : "confirm";
			nav.go({ to: `/auth/${path}`, search: nav.location.search, replace: true });
		}
	});
};

export const useMutateValidate = () => {
	const auth = useStoreAuth();
	const start = useStart();
	const nav = useNav();

	return useMutation({
		mutationKey: ["auth-validate"],
		mutationFn: () => {
			const body = { code: auth.code, token: auth.token, device: auth.device, ...auth.fillAccount() };
			if (start.user) body.userId = start.user.id;

			return api.auth.eval(body);
		},
		onSuccess: async () => {
			await authRequest(nav);
		}
	});
};

export const useMutateSignup = () => {
	const auth = useStoreAuth();
	const nav = useNav();

	return useMutation({
		mutationKey: ["auth-signup"],
		mutationFn: (body) => {
			const b = { ...body, code: auth.code, token: auth.token, device: auth.device, ...auth.fillAccount() };
			return api.auth.signup(b);
		},
		onSuccess: async () => {
			await authRequest(nav);
		}
	});
};

export const useMutateSecondary = () => {
	const auth = useStoreAuth();
	const nav = useNav();

	return useMutation({
		mutationKey: ["auth-secondary"],
		mutationFn: (body) => {
			const payload = { ...body };
			payload.device = auth.device;

			if (payload.email) auth.setToEmail(payload.email);
			else if (payload.phone) {
				auth.setToPhone(payload.phone);
				payload.phone = auth.sanitizePhone(payload.phone);
			};

			return api.auth.otp(payload);
		},
		onSuccess: (data) => {
			auth.setToken(data.token);
			nav.go({ to: `/auth/update?redirect_uri=${encodeURIComponent(nav.location.pathname)}` });
		}
	});
};

export const useMutateLogout = () => {
	const auth = useStoreAuth();
	const nav = useNav();

	return useMutation({
		mutationKey: ["auth-logout"],
		mutationFn: () => api.auth.logout({ device: auth.device }),
		onSuccess: () => nav.open("/auth")
	});
};